import { DEFAULT_ACTIVE_APP_ID, type AppRegistryEntry } from '../appsRegistry';
import type { Identity } from '../../api/tokenStorage';

/**
 * Determines the recommended startup app for a user based on a priority chain:
 *
 * 1. Last app the user explicitly opened (`identity.lastUsedAppId`) — if still installed.
 * 2. Security group or company default (`identity.defaultAppId`) — if installed.
 * 3. First installed app in registry order.
 * 4. DEFAULT_ACTIVE_APP_ID (fbr-smart) as the final hard fallback.
 *
 * Returns `undefined` only when no installed apps exist at all.
 */
export function resolveStartupApp(
    identity: Identity | null,
    installedAppIds: string[],
    registry: AppRegistryEntry[]
): AppRegistryEntry | undefined {
    if (installedAppIds.length === 0) return undefined;

    const installed = (id: string) => installedAppIds.includes(id);
    const findApp = (id: string | null | undefined): AppRegistryEntry | undefined =>
        id ? registry.find(a => a.id === id) : undefined;

    // 1. Last used app if still installed
    const lastUsed = identity?.lastUsedAppId;
    if (lastUsed && installed(lastUsed)) return findApp(lastUsed);

    // 2. Group/company default if installed
    const defaultApp = identity?.defaultAppId;
    if (defaultApp && installed(defaultApp)) return findApp(defaultApp);

    // 3. First installed app by registry order
    const first = registry.find(a => installed(a.id));
    if (first) return first;

    // 4. DEFAULT_ACTIVE_APP_ID as hard fallback
    return findApp(DEFAULT_ACTIVE_APP_ID);
}
